import { useQuery } from '@tanstack/react-query';
import { Link2 } from 'lucide-react';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * "N active links can see this folder" — the standing disclosure affordance.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS AT ALL
 *
 * Every other sharing action in the Vault is explicit: you pick a file and
 * share it. A Fias Transfer *folder subscription* is the one mechanism where a
 * document becomes externally visible with NO sharing action — someone drops a
 * file into a subscribed folder weeks later and whoever holds the link can
 * read it immediately.
 *
 * That is what a data room needs, and it is also an accidental-disclosure
 * vector. The control is that the answer to "who can see this?" must be
 * available while you are LOOKING at the folder — not only by auditing every
 * transfer you ever created. Hence a badge on the folder itself.
 *
 * The plan made this a hard condition of shipping dynamic membership, not a
 * nice-to-have.
 * ---------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------
 * IT MUST NOT CRY WOLF
 *
 * The server counts only links that would resolve RIGHT NOW — active,
 * unexpired, with authorizations remaining. A revoked or expired link exposes
 * nothing, and counting it would train the user to ignore the badge, which is
 * strictly worse than having no badge.
 *
 * For the same reason this renders nothing at zero. A badge that is always
 * present carries no signal.
 * ---------------------------------------------------------------------------
 */
export function FolderExposureBadge({
  folderId,
  exposures,
}: {
  folderId: string;
  /** Sparse holder-wide map from `useFolderExposures`. */
  exposures: Record<string, number> | undefined;
}) {
  const count = exposures?.[folderId] ?? 0;
  if (count <= 0) return null;

  const label = `${count} active transfer ${count === 1 ? 'link' : 'links'} can see this folder`;

  return (
    <span
      // Amber, not red: this is a state the user chose, not an error. Red here
      // would read as "something is wrong" and get dismissed.
      className="ml-auto flex shrink-0 items-center gap-0.5 rounded bg-amber-500/15 px-1 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400"
      title={label}
      aria-label={label}
    >
      <Link2 className="h-3 w-3" aria-hidden="true" />
      {count}
    </span>
  );
}

/**
 * The holder's whole exposure map, in one request.
 *
 * Sparse and fetched once per holder rather than per folder: the tree can hold
 * hundreds of nodes, and a per-node query would be that many round trips for a
 * badge that is usually absent.
 *
 * Failure is silent by design — `retry: false`, no error surface. If Fias
 * Transfer is unreachable the badge simply does not render. It is an advisory
 * annotation on someone else's page, and it must never break the Vault browser
 * or show an error the user cannot act on. The ACCESS control lives on the
 * serving path; this is only the notification.
 */
export function useFolderExposures(holderId: string | null) {
  const { data } = useQuery({
    queryKey: queryKeys.fiasTransfer.folderExposures(holderId ?? ''),
    queryFn: () => api.getFiasTransferFolderExposures(holderId!),
    enabled: !!holderId,
    retry: false,
    // Exposure changes only when someone issues or revokes a link — not
    // something to re-poll while browsing files.
    staleTime: 60_000,
  });
  return data?.exposures;
}
